// Travel Module Component - FASE 6
import React, { useState, useEffect } from 'react';
import SectionAgent from './SectionAgent';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plane, Map, Calendar, DollarSign, Users, Plus, Mic, MicOff,
  Navigation, MapPin, Clock, Car, Hotel, Utensils, Camera,
  Bus, Train, Save, Share2, History, ExternalLink, Play, Image as ImageIcon, Star
} from 'lucide-react';
import ShareModal from './ShareModal';
import Lightbox from './Lightbox';
import AIAgentModule from './AIAgentModule';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelModule = ({ user }) => {
  const [activeTab, setActiveTab] = useState('trips');
  const [trips, setTrips] = useState([]);
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [listening, setListening] = useState(false);
  const [generatedItinerary, setGeneratedItinerary] = useState(null);
  const [routeResult, setRouteResult] = useState(null);
  const [alternativeRoutes, setAlternativeRoutes] = useState(null);
  const [travelProposals, setTravelProposals] = useState(null);
  const [shareModal, setShareModal] = useState({ open: false, item: null, type: '' });
  const [lightbox, setLightbox] = useState({ open: false, content: null, type: null });
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [transportMode, setTransportMode] = useState('car'); // car, bus

  // Forms
  const [newTrip, setNewTrip] = useState({ 
    name: '', destination: '', start_date: '', end_date: '', budget: '', travelers: 1 
  });
  const [itineraryParams, setItineraryParams] = useState({ 
    destination: '', 
    days: 3, 
    interests: [], 
    budget: 'moderate',
    start_date: '',
    end_date: '',
    travel_style: 'balanced',
    accommodation: 'hotel',
    travelers: 1
  });
  const [routeParams, setRouteParams] = useState({ origin: '', destination: '' });

  useEffect(() => {
    loadTrips();
    loadSavedRoutes();
  }, [user.id]);

  const loadTrips = async () => {
    try {
      const res = await axios.get(`${API}/travel/trips/${user.id}`);
      setTrips(res.data);
    } catch (e) {}
  };

  const loadSavedRoutes = async () => {
    try {
      const res = await axios.get(`${API}/travel/routes/${user.id}`);
      setSavedRoutes(res.data);
    } catch (e) {}
  };

  // Get current location using browser geolocation
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Tu navegador no soporta geolocalización');
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Try to reverse geocode the coordinates
          const { latitude, longitude } = position.coords;
          // Use a simple reverse geocode with nominatim
          const res = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
          const address = res.data?.address;
          const locationName = address?.city || address?.town || address?.municipality || address?.state || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          setRouteParams({ ...routeParams, origin: locationName });
          toast.success(`Ubicación: ${locationName}`);
        } catch (e) {
          // Fallback to coordinates
          setRouteParams({ 
            ...routeParams, 
            origin: `${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}` 
          });
        }
        setGettingLocation(false);
      },
      (error) => {
        toast.error('No se pudo obtener tu ubicación');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Get REAL destination images from Unsplash
  const getDestinationImage = (destination, size = '800x600') => {
    const cleanDest = destination?.replace(/[^a-zA-Z\s]/g, '').trim().toLowerCase() || 'travel';
    return `https://source.unsplash.com/${size}/?${encodeURIComponent(cleanDest)},travel,landmark`;
  };

  // Get hotel image
  const getHotelImage = (hotelName, destination) => {
    return `https://source.unsplash.com/400x300/?hotel,luxury,resort`;
  };

  // Get attraction image
  const getAttractionImage = (attractionName, destination) => {
    const searchTerms = `${attractionName || ''} ${destination || ''} tourist attraction`.trim();
    return `https://source.unsplash.com/400x300/?${encodeURIComponent(searchTerms)}`;
  };

  const createTrip = async () => {
    if (!newTrip.name || !newTrip.destination) return;
    try {
      await axios.post(`${API}/travel/trips?user_id=${user.id}`, newTrip);
      toast.success('Viaje creado');
      setNewTrip({ name: '', destination: '', start_date: '', end_date: '', budget: '', travelers: 1 });
      loadTrips();
    } catch (e) {
      toast.error('Error');
    }
  };

  const generateItinerary = async () => {
    if (!itineraryParams.destination) return;
    setLoading(true);
    try {
      toast.info('Generando itinerario con IA...');
      const res = await axios.post(`${API}/travel/generate-itinerary?user_id=${user.id}`, null, {
        params: {
          destination: itineraryParams.destination,
          days: itineraryParams.days,
          budget: itineraryParams.budget
        }
      });
      setGeneratedItinerary(res.data);
      toast.success('Itinerario generado');
    } catch (e) {
      toast.error('Error al generar itinerario');
    } finally {
      setLoading(false);
    }
  };

  const getTravelProposals = async () => {
    if (!itineraryParams.destination) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/travel/proposals`, {
        params: {
          destination: itineraryParams.destination,
          departure_date: new Date().toISOString().split('T')[0],
          travelers: newTrip.travelers || 1
        }
      });
      setTravelProposals(res.data);
    } catch (e) {
      toast.error('Error al obtener propuestas');
    } finally {
      setLoading(false);
    }
  };

  const calculateRoute = async () => {
    if (!routeParams.origin || !routeParams.destination) return;
    setLoading(true);
    try {
      const [routeRes, altRes] = await Promise.all([
        axios.get(`${API}/travel/route`, { params: routeParams }),
        axios.get(`${API}/travel/route/alternatives`, { params: routeParams })
      ]);
      setRouteResult(routeRes.data);
      setAlternativeRoutes(altRes.data);
    } catch (e) {
      toast.error('Error');
    } finally {
      setLoading(false);
    }
  };

  const saveRoute = async () => {
    if (!routeResult) return;
    try {
      await axios.post(`${API}/travel/routes/save?user_id=${user.id}`, routeResult);
      toast.success('Ruta guardada');
      loadSavedRoutes();
    } catch (e) {
      toast.error('Error al guardar ruta');
    }
  };

  const handleVoice = () => {
    if (!('webkitSpeechRecognition' in window)) return;
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = 'es-MX';
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setItineraryParams({ ...itineraryParams, destination: text });
    };
    listening ? recognition.stop() : recognition.start();
  };

  const interests = ['Cultura', 'Naturaleza', 'Aventura', 'Gastronomía', 'Historia', 'Playas', 'Montañas', 'Ciudades'];

  // Pide Chofer Sub-Component
  const PideChoferSection = ({ userId }) => {
    const [origin, setOrigin] = useState('');
    const [destination, setDestination] = useState('');
    const [results, setResults] = useState(null);
    const [loadingChofer, setLoadingChofer] = useState(false);
    const [gettingLocation, setGettingLocation] = useState(false);

    const getMyLocation = () => {
      if (!navigator.geolocation) {
        toast.error('Tu navegador no soporta geolocalización');
        return;
      }
      setGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const { latitude, longitude } = pos.coords;
          try {
            // Try to reverse geocode
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();
            const address = data.display_name?.split(',').slice(0, 2).join(',') || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
            setOrigin(address);
            toast.success('Ubicación obtenida');
          } catch (e) {
            setOrigin(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          }
          setGettingLocation(false);
        },
        (err) => {
          toast.error('No se pudo obtener tu ubicación');
          setGettingLocation(false);
        }
      );
    };

    const searchRides = async () => {
      if (!origin || !destination) {
        toast.error('Ingresa origen y destino');
        return;
      }
      setLoadingChofer(true);
      
      // Simulated results for different ride services
      setTimeout(() => {
        setResults({
          uber: [
            { type: 'UberX', price: Math.floor(Math.random() * 100) + 80, time: '5 min', color: 'bg-black' },
            { type: 'Uber Comfort', price: Math.floor(Math.random() * 150) + 120, time: '7 min', color: 'bg-black' },
            { type: 'Uber Black', price: Math.floor(Math.random() * 200) + 200, time: '10 min', color: 'bg-black' },
            { type: 'Uber XL', price: Math.floor(Math.random() * 180) + 150, time: '8 min', color: 'bg-black' },
          ],
          didi: [
            { type: 'DiDi Express', price: Math.floor(Math.random() * 90) + 70, time: '4 min', color: 'bg-orange-500' },
            { type: 'DiDi Fleet', price: Math.floor(Math.random() * 130) + 100, time: '6 min', color: 'bg-orange-500' },
          ],
          taxi: [
            { type: 'Taxi Sitio', price: Math.floor(Math.random() * 120) + 100, time: '3 min', color: 'bg-yellow-500' },
            { type: 'Taxi Libre', price: Math.floor(Math.random() * 100) + 80, time: '1 min', color: 'bg-yellow-500' },
          ]
        });
        setLoadingChofer(false);
      }, 1500);
    };

    const getBestOption = () => {
      if (!results) return null;
      const allOptions = [
        ...results.uber.map(o => ({ ...o, service: 'Uber' })),
        ...results.didi.map(o => ({ ...o, service: 'DiDi' })),
        ...results.taxi.map(o => ({ ...o, service: 'Taxi' }))
      ];
      return allOptions.sort((a, b) => a.price - b.price)[0];
    };

    return (
      <>
      <SectionAgent section="travel" />
      <div className="space-y-6">
        {/* Header with Logos */}
        <div className="glass-panel p-6">
          <div className="flex items-center justify-center gap-8 mb-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-2">
                <span className="text-white text-2xl font-bold">U</span>
              </div>
              <span className="text-gray-400 text-xs">Uber</span>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mb-2">
                <span className="text-white text-2xl font-bold">D</span>
              </div>
              <span className="text-gray-400 text-xs">DiDi</span>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-500 rounded-2xl flex items-center justify-center mb-2">
                <Car className="text-black w-8 h-8" />
              </div>
              <span className="text-gray-400 text-xs">Taxi</span>
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-white text-center mb-2">Pide Chofer</h2>
          <p className="text-gray-400 text-center mb-6">Encuentra el mejor costo y la mejor ruta</p>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Origen</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={origin} 
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="¿Dónde te recogemos?"
                  className="input-glass flex-1"
                />
                <button 
                  onClick={getMyLocation}
                  disabled={gettingLocation}
                  className="btn-secondary px-4"
                >
                  {gettingLocation ? '...' : <MapPin size={18} />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="text-gray-400 text-sm mb-2 block">Destino</label>
              <input 
                type="text" 
                value={destination} 
                onChange={(e) => setDestination(e.target.value)}
                placeholder="¿A dónde vas?"
                className="input-glass w-full"
              />
            </div>
            
            <button 
              onClick={searchRides}
              disabled={loadingChofer}
              className="btn-primary w-full py-4"
            >
              {loadingChofer ? 'Buscando...' : 'Buscar Mejor Costo'}
            </button>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-4">
            {/* Best Option */}
            {getBestOption() && (
              <div className="glass-panel p-6 border-2 border-green-500/30 bg-green-500/5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-green-500 text-white text-xs rounded-full font-semibold">MEJOR PRECIO</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${getBestOption().color} rounded-xl flex items-center justify-center`}>
                      <Car className="text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{getBestOption().service} - {getBestOption().type}</p>
                      <p className="text-gray-400 text-sm">{getBestOption().time} de espera</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-green-400">${getBestOption().price}</p>
                    <p className="text-gray-500 text-sm">MXN</p>
                  </div>
                </div>
              </div>
            )}

            {/* Uber Options */}
            <div className="glass-panel p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold">U</span>
                </div>
                <h3 className="text-white font-semibold">Uber</h3>
              </div>
              <div className="space-y-2">
                {results.uber.map((option, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-black/20 rounded-lg hover:bg-black/30 transition-colors">
                    <div>
                      <p className="text-white">{option.type}</p>
                      <p className="text-gray-500 text-sm">{option.time}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-white font-semibold">${option.price}</p>
                      <a 
                        href={`uber://?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(destination)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 bg-black text-white rounded-lg text-sm hover:bg-gray-800 transition-colors flex items-center gap-1"
                      >
                        <ExternalLink size={12} />Pedir
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* DiDi Options */}
            <div className="glass-panel p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold">D</span>
                </div>
                <h3 className="text-white font-semibold">DiDi</h3>
              </div>
              <div className="space-y-2">
                {results.didi.map((option, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-black/20 rounded-lg hover:bg-black/30 transition-colors">
                    <div>
                      <p className="text-white">{option.type}</p>
                      <p className="text-gray-500 text-sm">{option.time}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-white font-semibold">${option.price}</p>
                      <a 
                        href={`https://d.didiglobal.com/passengerApp?destination=${encodeURIComponent(destination)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 transition-colors flex items-center gap-1"
                      >
                        <ExternalLink size={12} />Pedir
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Taxi Options */}
            <div className="glass-panel p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-yellow-500 rounded-xl flex items-center justify-center">
                  <Car className="text-black" />
                </div>
                <h3 className="text-white font-semibold">Taxi</h3>
              </div>
              <div className="space-y-2">
                {results.taxi.map((option, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-black/20 rounded-lg hover:bg-black/30 transition-colors">
                    <div>
                      <p className="text-white">{option.type}</p>
                      <p className="text-gray-500 text-sm">{option.time}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-white font-semibold">${option.price}</p>
                      <a 
                        href={`tel:+5255551234`}
                        className="px-3 py-1 bg-yellow-500 text-black rounded-lg text-sm hover:bg-yellow-400 transition-colors flex items-center gap-1"
                      >
                        <ExternalLink size={12} />Llamar
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    
      </>
    );
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-white" data-testid="travel-title">Viajes</h1>
        <button onClick={handleVoice} className={`p-3 rounded-xl transition-colors ${listening ? 'bg-red-500' : 'bg-white/10 hover:bg-white/20'}`}>
          {listening ? <MicOff className="text-white" /> : <Mic className="text-white" />}
        </button>
      </div>

      {/* Tabs - scrollable on mobile */}
      <div className="flex gap-2 mb-6 flex-wrap overflow-x-auto pb-2">
        <button onClick={() => setActiveTab('trips')} className={`tab-button whitespace-nowrap ${activeTab === 'trips' ? 'active' : 'inactive'}`}>
          <Plane size={14} />Mis Viajes
        </button>
        <button onClick={() => setActiveTab('itinerary')} className={`tab-button whitespace-nowrap ${activeTab === 'itinerary' ? 'active' : 'inactive'}`}>
          <Calendar size={14} />Itinerarios
        </button>
        <button onClick={() => setActiveTab('routes')} className={`tab-button whitespace-nowrap ${activeTab === 'routes' ? 'active' : 'inactive'}`}>
          <Navigation size={14} />Rutas
        </button>
        <button onClick={() => setActiveTab('chofer')} className={`tab-button whitespace-nowrap ${activeTab === 'chofer' ? 'active' : 'inactive'}`}>
          <Car size={14} />Pide Chofer
        </button>
        <button onClick={() => setActiveTab('history')} className={`tab-button whitespace-nowrap ${activeTab === 'history' ? 'active' : 'inactive'}`}>
          <History size={14} />Historial
        </button>
      </div>

      {/* Trips Tab */}
      {activeTab === 'trips' && (
        <div className="space-y-6">
          {/* Create Trip Form */}
          <div className="glass-panel p-4 md:p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Plus className="text-red-400" />
              Nuevo Viaje
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-4">
              <input type="text" value={newTrip.name} onChange={(e) => setNewTrip({ ...newTrip, name: e.target.value })}
                placeholder="Nombre del viaje" className="input-glass" />
              <input type="text" value={newTrip.destination} onChange={(e) => setNewTrip({ ...newTrip, destination: e.target.value })}
                placeholder="Destino" className="input-glass" />
              <input type="number" value={newTrip.travelers} onChange={(e) => setNewTrip({ ...newTrip, travelers: parseInt(e.target.value) })}
                placeholder="Viajeros" className="input-glass" min={1} />
              <input type="date" value={newTrip.start_date} onChange={(e) => setNewTrip({ ...newTrip, start_date: e.target.value })}
                className="input-glass" />
              <input type="date" value={newTrip.end_date} onChange={(e) => setNewTrip({ ...newTrip, end_date: e.target.value })}
                className="input-glass" />
              <input type="number" value={newTrip.budget} onChange={(e) => setNewTrip({ ...newTrip, budget: e.target.value })}
                placeholder="Presupuesto MXN" className="input-glass" />
            </div>
            <button onClick={createTrip} className="btn-primary">Crear Viaje</button>
          </div>

          {/* Trips List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {trips.map(trip => (
              <div key={trip.id} className="glass-panel overflow-hidden group">
                <div 
                  className="h-40 bg-cover bg-center relative cursor-pointer"
                  style={{ backgroundImage: `url(${getDestinationImage(trip.destination)})` }}
                  onClick={() => setLightbox({ open: true, content: getDestinationImage(trip.destination), type: 'image' })}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-xl font-bold text-white">{trip.name}</h3>
                    <p className="text-gray-300 flex items-center gap-2">
                      <MapPin size={14} />{trip.destination}
                    </p>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between text-sm text-gray-400 mb-3">
                    <span className="flex items-center gap-1"><Calendar size={14} />{trip.start_date}</span>
                    <span className="flex items-center gap-1"><Users size={14} />{trip.travelers} viajeros</span>
                    {trip.budget && <span className="flex items-center gap-1"><DollarSign size={14} />${parseInt(trip.budget).toLocaleString()}</span>}
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setShareModal({ open: true, item: trip, type: 'trip' })}
                      className="flex-1 btn-secondary text-sm flex items-center justify-center gap-2"
                    >
                      <Share2 size={14} />Compartir
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Itinerary Generator Tab */}
      {activeTab === 'itinerary' && (
        <div className="space-y-6">
          <div className="glass-panel p-4 md:p-6" data-testid="itinerary-form">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Calendar className="text-red-400" />
              Generador de Itinerarios con IA
            </h3>
            
            {/* Row 1: Destination & Travelers */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 md:gap-4 mb-4">
              <div className="sm:col-span-3">
                <label className="text-gray-400 text-xs mb-1 block">Destino</label>
                <input 
                  type="text" 
                  value={itineraryParams.destination} 
                  onChange={(e) => setItineraryParams({ ...itineraryParams, destination: e.target.value })}
                  placeholder="¿A dónde quieres ir? (ej: Cancún, París, Tokyo...)" 
                  className="input-glass w-full"
                  data-testid="itinerary-destination-input"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Viajeros</label>
                <input 
                  type="number" 
                  value={itineraryParams.travelers} 
                  onChange={(e) => setItineraryParams({ ...itineraryParams, travelers: parseInt(e.target.value) || 1 })}
                  className="input-glass w-full" 
                  min={1} 
                  max={20}
                />
              </div>
            </div>
            
            {/* Row 2: Dates & Days */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-4">
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Fecha de inicio</label>
                <input 
                  type="date" 
                  value={itineraryParams.start_date} 
                  onChange={(e) => setItineraryParams({ ...itineraryParams, start_date: e.target.value })}
                  className="input-glass w-full"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Fecha de fin</label>
                <input 
                  type="date" 
                  value={itineraryParams.end_date} 
                  onChange={(e) => setItineraryParams({ ...itineraryParams, end_date: e.target.value })}
                  className="input-glass w-full"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Días de viaje</label>
                <input 
                  type="number" 
                  value={itineraryParams.days} 
                  onChange={(e) => setItineraryParams({ ...itineraryParams, days: parseInt(e.target.value) || 1 })}
                  className="input-glass w-full" 
                  min={1} 
                  max={30}
                />
              </div>
            </div>
            
            {/* Row 3: Travel Style & Accommodation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4">
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Estilo de viaje</label>
                <select 
                  value={itineraryParams.travel_style} 
                  onChange={(e) => setItineraryParams({ ...itineraryParams, travel_style: e.target.value })}
                  className="input-glass w-full"
                >
                  <option value="relaxed">Relajado - Poco movimiento</option>
                  <option value="balanced">Balanceado - Mix de actividades y descanso</option>
                  <option value="adventure">Aventura - Mucha actividad</option>
                  <option value="romantic">Romántico - Parejas</option>
                  <option value="family">Familiar - Con niños</option>
                  <option value="backpacker">Mochilero - Low cost</option>
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Tipo de hospedaje preferido</label>
                <select 
                  value={itineraryParams.accommodation} 
                  onChange={(e) => setItineraryParams({ ...itineraryParams, accommodation: e.target.value })}
                  className="input-glass w-full"
                >
                  <option value="hotel">Hotel</option>
                  <option value="airbnb">Airbnb / Casa completa</option>
                  <option value="hostel">Hostal</option>
                  <option value="resort">Resort todo incluido</option>
                  <option value="boutique">Hotel boutique</option>
                </select>
              </div>
            </div>

            {/* Interests */}
            <div className="mb-4">
              <p className="text-gray-400 text-sm mb-2">Intereses:</p>
              <div className="flex flex-wrap gap-2">
                {interests.map(interest => (
                  <button key={interest}
                    onClick={() => {
                      const newInterests = itineraryParams.interests.includes(interest)
                        ? itineraryParams.interests.filter(i => i !== interest)
                        : [...itineraryParams.interests, interest];
                      setItineraryParams({ ...itineraryParams, interests: newInterests });
                    }}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      itineraryParams.interests.includes(interest)
                        ? 'bg-red-500 text-white'
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}>
                    {interest}
                  </button>
                ))}
              </div>
            </div>

            {/* Budget */}
            <div className="mb-4">
              <p className="text-gray-400 text-sm mb-2">Presupuesto:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'low', label: '💰 Económico', desc: '< $500 USD' },
                  { value: 'moderate', label: '💵 Moderado', desc: '$500-1500 USD' },
                  { value: 'luxury', label: '💎 Lujo', desc: '> $1500 USD' }
                ].map(budget => (
                  <button key={budget.value}
                    onClick={() => setItineraryParams({ ...itineraryParams, budget: budget.value })}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      itineraryParams.budget === budget.value
                        ? 'bg-red-500 text-white'
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}>
                    <span className="block">{budget.label}</span>
                    <span className="text-xs opacity-70">{budget.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <button 
                onClick={generateItinerary} 
                disabled={loading || !itineraryParams.destination} 
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="generate-itinerary-btn"
              >
                {loading ? <span className="animate-spin">◌</span> : <Calendar size={18} />}
                {loading ? 'Generando...' : 'Generar Itinerario con IA'}
              </button>
              <button 
                onClick={getTravelProposals} 
                disabled={loading || !itineraryParams.destination} 
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <DollarSign size={18} />Ver Propuestas de Costos
              </button>
            </div>
          </div>

          {/* Travel Proposals */}
          {travelProposals && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {/* Buses */}
              <div className="glass-panel p-4 md:p-6">
                <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Bus className="text-blue-400" />Autobuses
                </h4>
                <div className="space-y-3">
                  {travelProposals.buses?.map((bus, idx) => (
                    <div key={idx} className="p-3 bg-black/20 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium">{bus.company}</span>
                        <span className="text-green-400 font-bold">${bus.price}</span>
                      </div>
                      <p className="text-gray-500 text-sm">{bus.class} • {bus.duration} • Sale {bus.departure}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Flights */}
              <div className="glass-panel p-4 md:p-6">
                <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Plane className="text-blue-400" />Vuelos
                </h4>
                <div className="space-y-3">
                  {travelProposals.flights?.map((flight, idx) => (
                    <div key={idx} className="p-3 bg-black/20 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium">{flight.airline}</span>
                        <span className="text-green-400 font-bold">${flight.price}</span>
                      </div>
                      <p className="text-gray-500 text-sm">{flight.duration} • Sale {flight.departure} • {flight.stops === 0 ? 'Directo' : `${flight.stops} escalas`}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hotels */}
              <div className="glass-panel p-4 md:p-6">
                <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Hotel className="text-blue-400" />Hospedaje
                </h4>
                <div className="space-y-3">
                  {travelProposals.hotels?.map((hotel, idx) => (
                    <div key={idx} className="p-3 bg-black/20 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium truncate">{hotel.name}</span>
                        <span className="text-green-400 font-bold whitespace-nowrap">${hotel.price_per_night}/noche</span>
                      </div>
                      <p className="text-gray-500 text-sm">{'⭐'.repeat(hotel.stars)} • {hotel.rating} rating</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Generated Itinerary with Photos */}
          {generatedItinerary && (
            <div className="glass-panel p-4 md:p-6">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <Calendar className="text-red-400" />
                  Itinerario para {generatedItinerary.destination} ({generatedItinerary.days} días)
                </h4>
                <button 
                  onClick={() => setShareModal({ open: true, item: generatedItinerary, type: 'itinerary' })}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Share2 size={16} />Compartir
                </button>
              </div>
              
              {/* Destination Hero Photo */}
              <div 
                className="relative h-48 md:h-64 rounded-xl overflow-hidden mb-6 cursor-pointer"
                onClick={() => setLightbox({ open: true, content: `https://picsum.photos/seed/${generatedItinerary.destination}/1200/600`, type: 'image' })}
              >
                <img 
                  src={`https://picsum.photos/seed/${generatedItinerary.destination}/1200/600`}
                  alt={generatedItinerary.destination}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <h4 className="text-2xl font-bold text-white">{generatedItinerary.destination}</h4>
                  <p className="text-gray-300">{generatedItinerary.days} días de aventura</p>
                </div>
              </div>
              
              {/* Quick Info Cards with Photos */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Hotel Recommendation */}
                <div className="bg-black/20 rounded-xl overflow-hidden">
                  <div 
                    className="h-32 cursor-pointer relative"
                    onClick={() => setLightbox({ open: true, content: `https://picsum.photos/seed/hotel-${generatedItinerary.destination}/600/400`, type: 'image' })}
                  >
                    <img 
                      src={`https://picsum.photos/seed/hotel-${generatedItinerary.destination}/600/400`}
                      alt="Hotel"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded-full text-xs text-white flex items-center gap-1">
                      <Hotel size={12} />Hotel
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-white font-medium text-sm">Hotel Recomendado</p>
                    <p className="text-gray-500 text-xs">⭐⭐⭐⭐ • Desde $1,200/noche</p>
                    <a href={`https://booking.com/searchresults.html?ss=${encodeURIComponent(generatedItinerary.destination)}`} 
                       target="_blank" rel="noopener noreferrer"
                       className="mt-2 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      Ver hoteles <ExternalLink size={10} />
                    </a>
                  </div>
                </div>
                
                {/* Flight/Airline */}
                <div className="bg-black/20 rounded-xl overflow-hidden">
                  <div 
                    className="h-32 cursor-pointer relative"
                    onClick={() => setLightbox({ open: true, content: `https://picsum.photos/seed/flight-${generatedItinerary.destination}/600/400`, type: 'image' })}
                  >
                    <img 
                      src={`https://picsum.photos/seed/flight-${generatedItinerary.destination}/600/400`}
                      alt="Vuelo"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded-full text-xs text-white flex items-center gap-1">
                      <Plane size={12} />Vuelo
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-white font-medium text-sm">Volaris / VivaAerobus</p>
                    <p className="text-gray-500 text-xs">Desde $899 MXN</p>
                    <a href={`https://www.google.com/travel/flights?q=vuelos+a+${encodeURIComponent(generatedItinerary.destination)}`} 
                       target="_blank" rel="noopener noreferrer"
                       className="mt-2 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      Buscar vuelos <ExternalLink size={10} />
                    </a>
                  </div>
                </div>
                
                {/* Attraction */}
                <div className="bg-black/20 rounded-xl overflow-hidden">
                  <div 
                    className="h-32 cursor-pointer relative"
                    onClick={() => setLightbox({ open: true, content: `https://picsum.photos/seed/place-${generatedItinerary.destination}/600/400`, type: 'image' })}
                  >
                    <img 
                      src={`https://picsum.photos/seed/place-${generatedItinerary.destination}/600/400`}
                      alt="Atracción"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 rounded-full text-xs text-white flex items-center gap-1">
                      <Star size={12} />Top
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-white font-medium text-sm">Atracciones Populares</p>
                    <p className="text-gray-500 text-xs">⭐ 4.8 • Miles de reseñas</p>
                    <a href={`https://www.tripadvisor.com/Search?q=${encodeURIComponent(generatedItinerary.destination)}`} 
                       target="_blank" rel="noopener noreferrer"
                       className="mt-2 text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      Ver actividades <ExternalLink size={10} />
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Itinerary Text */}
              <div className="prose prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-gray-300 text-sm bg-black/20 p-4 rounded-lg">
                  {generatedItinerary.itinerary}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Routes Tab */}
      {activeTab === 'routes' && (
        <div className="space-y-6">
          <div className="glass-panel p-4 md:p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Navigation className="text-red-400" />
              Calculador de Rutas
            </h3>
            
            {/* Transport Mode Selector */}
            <div className="flex gap-2 mb-4">
              <button 
                onClick={() => setTransportMode('car')}
                className={`flex-1 p-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                  transportMode === 'car' ? 'bg-red-500 text-white' : 'bg-white/10 text-gray-400 hover:bg-white/20'
                }`}
              >
                <Car size={18} />Auto
              </button>
              <button 
                onClick={() => setTransportMode('bus')}
                className={`flex-1 p-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                  transportMode === 'bus' ? 'bg-red-500 text-white' : 'bg-white/10 text-gray-400 hover:bg-white/20'
                }`}
              >
                <Bus size={18} />Autobús
              </button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-4">
              <div className="relative">
                <input type="text" value={routeParams.origin} onChange={(e) => setRouteParams({ ...routeParams, origin: e.target.value })}
                  placeholder="Origen (ej: CDMX)" className="input-glass pr-12 w-full" />
                <button 
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-red-400 hover:bg-red-500/20 rounded-lg"
                  title="Usar mi ubicación"
                >
                  {gettingLocation ? <span className="animate-spin">◌</span> : <MapPin size={18} />}
                </button>
              </div>
              <input type="text" value={routeParams.destination} onChange={(e) => setRouteParams({ ...routeParams, destination: e.target.value })}
                placeholder="Destino (ej: Guadalajara)" className="input-glass" />
            </div>
            <button onClick={calculateRoute} disabled={loading} className="btn-primary w-full sm:w-auto">
              {loading ? 'Calculando...' : 'Calcular Ruta'}
            </button>
          </div>
          
          {/* Bus Options (when bus mode selected and route calculated) */}
          {transportMode === 'bus' && routeResult && (
            <div className="glass-panel p-4 md:p-6">
              <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Bus className="text-blue-400" />
                Opciones de Autobús
              </h4>
              <div className="space-y-3">
                {[
                  { company: 'ETN', class: 'Ejecutiva', price: Math.round(routeResult.distance_km * 1.2), duration: Math.round(routeResult.duration_hours * 1.1) + 'h', departure: '06:00', url: 'https://etn.com.mx' },
                  { company: 'Primera Plus', class: 'Primera', price: Math.round(routeResult.distance_km * 1.0), duration: Math.round(routeResult.duration_hours * 1.15) + 'h', departure: '08:30', url: 'https://primeraplus.com.mx' },
                  { company: 'Pullman de México', class: 'Económica', price: Math.round(routeResult.distance_km * 0.8), duration: Math.round(routeResult.duration_hours * 1.2) + 'h', departure: '10:00', url: 'https://pullmandemexicoexpress.com.mx' },
                  { company: 'ADO', class: 'GL', price: Math.round(routeResult.distance_km * 1.3), duration: Math.round(routeResult.duration_hours * 1.1) + 'h', departure: '12:00', url: 'https://ado.com.mx' },
                ].map((bus, idx) => (
                  <div key={idx} className="p-4 bg-black/20 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">{bus.company}</p>
                      <p className="text-gray-500 text-sm">{bus.class} • {bus.duration} • Sale {bus.departure}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-green-400 font-bold">${bus.price}</p>
                      <a 
                        href={bus.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors flex items-center gap-1"
                      >
                        <ExternalLink size={12} />Comprar
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Route Result */}
          {routeResult && transportMode === 'car' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              <div className="glass-panel p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <h4 className="text-white font-semibold">Ruta Principal</h4>
                  <div className="flex gap-2">
                    <button onClick={saveRoute} className="p-2 text-green-400 hover:bg-green-500/20 rounded-lg" title="Guardar">
                      <Save size={18} />
                    </button>
                    <button 
                      onClick={() => setShareModal({ open: true, item: routeResult, type: 'route' })}
                      className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg" title="Compartir"
                    >
                      <Share2 size={18} />
                    </button>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2 md:gap-4">
                    <MapPin className="text-green-400 flex-shrink-0" />
                    <span className="text-white">{routeResult.origin}</span>
                    <span className="text-gray-600">→</span>
                    <MapPin className="text-red-400 flex-shrink-0" />
                    <span className="text-white">{routeResult.destination}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    <div className="p-3 md:p-4 bg-black/20 rounded-lg">
                      <p className="text-gray-500 text-sm">Distancia</p>
                      <p className="text-xl md:text-2xl font-bold text-white">{routeResult.distance_km} km</p>
                    </div>
                    <div className="p-3 md:p-4 bg-black/20 rounded-lg">
                      <p className="text-gray-500 text-sm">Duración</p>
                      <p className="text-xl md:text-2xl font-bold text-white">{routeResult.duration_hours} hrs</p>
                    </div>
                    <div className="p-3 md:p-4 bg-black/20 rounded-lg">
                      <p className="text-gray-500 text-sm">Gasolina</p>
                      <p className="text-lg md:text-xl font-bold text-white">{routeResult.estimated_fuel_liters} L</p>
                      <p className="text-green-400 text-sm">${routeResult.estimated_fuel_cost}</p>
                    </div>
                    <div className="p-3 md:p-4 bg-black/20 rounded-lg">
                      <p className="text-gray-500 text-sm">Casetas</p>
                      <p className="text-lg md:text-xl font-bold text-white">${routeResult.tolls_estimated}</p>
                    </div>
                  </div>

                  {/* Map Embed */}
                  <div 
                    className="h-48 rounded-lg overflow-hidden cursor-pointer bg-gradient-to-br from-blue-900/50 to-green-900/50 flex items-center justify-center"
                    onClick={() => setLightbox({ open: true, content: `${routeResult.origin} to ${routeResult.destination}`, type: 'map' })}
                  >
                    <div className="text-center">
                      <Map className="text-blue-400 mx-auto mb-2" size={40} />
                      <p className="text-white">Ver mapa de la ruta</p>
                    </div>
                  </div>

                  <a href={routeResult.maps_url} target="_blank" rel="noopener noreferrer" className="btn-secondary w-full flex items-center justify-center gap-2">
                    <ExternalLink size={18} />Abrir en Google Maps
                  </a>
                </div>
              </div>

              {/* Alternative Routes */}
              {alternativeRoutes && (
                <div className="glass-panel p-6">
                  <h4 className="text-white font-semibold mb-4">Rutas Alternativas</h4>
                  <div className="space-y-4">
                    {alternativeRoutes.routes?.map((route, idx) => (
                      <div key={idx} className={`p-4 rounded-lg ${idx === 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-black/20'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">{route.name}</span>
                          {idx === 0 && <span className="text-green-400 text-xs">Recomendada</span>}
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <p className="text-gray-500">Distancia</p>
                            <p className="text-white">{route.distance_km} km</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Duración</p>
                            <p className="text-white">{route.duration_hours} hrs</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Casetas</p>
                            <p className="text-white">${route.toll_cost}</p>
                          </div>
                        </div>
                        {route.highlights && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {route.highlights.map((h, i) => (
                              <span key={i} className="px-2 py-0.5 bg-white/10 rounded text-xs text-gray-400">{h}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-6">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <History className="text-red-400" />
            Rutas Guardadas
          </h3>
          {savedRoutes.length === 0 ? (
            <div className="glass-panel p-8 md:p-12 text-center">
              <Navigation className="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">Sin rutas guardadas</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {savedRoutes.map((route, idx) => (
                <div key={idx} className="glass-panel p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <MapPin className="text-green-400 flex-shrink-0" />
                      <span className="text-white">{route.origin}</span>
                      <span className="text-gray-600">→</span>
                      <MapPin className="text-red-400 flex-shrink-0" />
                      <span className="text-white">{route.destination}</span>
                    </div>
                    <button 
                      onClick={() => setShareModal({ open: true, item: route, type: 'route' })}
                      className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg self-end sm:self-auto"
                    >
                      <Share2 size={16} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-3 md:gap-4 text-sm text-gray-400">
                    <span>{route.distance_km} km</span>
                    <span>{route.duration_hours} hrs</span>
                    <span>Guardada: {new Date(route.saved_at).toLocaleDateString('es-MX')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pide Chofer Tab */}
      {activeTab === 'chofer' && (
        <PideChoferSection userId={user.id} />
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModal.open}
        onClose={() => setShareModal({ open: false, item: null, type: '' })}
        itemType={shareModal.type}
        itemId={shareModal.item?.id}
        itemTitle={shareModal.item?.name || shareModal.item?.destination || 'Viaje'}
        itemData={{ userId: user.id }}
      />

      {/* Lightbox */}
      <Lightbox
        isOpen={lightbox.open}
        onClose={() => setLightbox({ open: false, content: null, type: null })}
        content={lightbox.content}
        type={lightbox.type}
      />

      <AIAgentModule sectionContext="viajes, vuelos, hoteles e itinerarios" compact={true} placeholder="Planifica tu próximo viaje con IA..." />
    </div>
  );
};

export default TravelModule;
